import type { ICreateWhatsNewParams } from '../Services/WhatsNew.types.service';
import type { WhatsNewFormData, Language } from '../Types/WhatsNewForm.types';

// ---------------------------------------------------------------------------
// Shared helpers — build translation arrays for the API
// ---------------------------------------------------------------------------

function buildTranslationArray(formData: WhatsNewFormData) {
  return (['pt', 'en', 'es'] as Language[]).map(lang => ({
    language: lang,
    welcomeMessage: formData.welcomeMessage[lang],
    ...(formData.subtitle[lang] ? { subtitle: formData.subtitle[lang] } : {}),
    ...(formData.footer[lang] ? { footer: formData.footer[lang] } : {}),
    ...(formData.confirmButtonText[lang]
      ? { confirmButtonText: formData.confirmButtonText[lang] }
      : {}),
  }));
}

function buildItemPayloads(formData: WhatsNewFormData) {
  return formData.items.map(item => ({
    icon: item.icon,
    title: item.title, // Primary language (PT)
    body: item.body, // Primary language (PT)
    order: item.order,
    color: item.color,
    privileges: item.privileges ?? [],
    translations: (['pt', 'en', 'es'] as Language[]).map(lang => ({
      language: lang,
      title: item.translations[lang].title,
      body: item.translations[lang].body,
    })),
  }));
}

// ---------------------------------------------------------------------------
// Public — used by ConfirmationModal (create)
// ---------------------------------------------------------------------------

export function buildCreatePayload(formData: WhatsNewFormData): ICreateWhatsNewParams {
  return {
    platformVersionId: formData.platformVersionId,
    welcomeMessage: formData.welcomeMessage.pt, // Primary language
    ...(formData.subtitle.pt ? { subtitle: formData.subtitle.pt } : {}),
    ...(formData.footer.pt ? { footer: formData.footer.pt } : {}),
    ...(formData.confirmButtonText.pt ? { confirmButtonText: formData.confirmButtonText.pt } : {}),
    tenantSlug: formData.tenantSlug,
    publishedAt: formData.publishedAt,
    ...(formData.useCaseSlug ? { useCaseSlug: formData.useCaseSlug } : {}),
    ...(formData.pageId ? { pageId: formData.pageId } : {}),
    ...(formData.actionParams ? { actionParams: formData.actionParams } : {}),
    translations: buildTranslationArray(formData),
    items: buildItemPayloads(formData),
  };
}

// ---------------------------------------------------------------------------
// Public — used by the creation flow in edit mode
// ---------------------------------------------------------------------------

export function buildUpdatePayload(formData: WhatsNewFormData, useCaseSlug: string | null) {
  return {
    platformVersionId: formData.platformVersionId,
    welcomeMessage: formData.welcomeMessage.pt, // Primary language
    ...(formData.subtitle.pt ? { subtitle: formData.subtitle.pt } : {}),
    ...(formData.footer.pt ? { footer: formData.footer.pt } : {}),
    ...(formData.confirmButtonText.pt ? { confirmButtonText: formData.confirmButtonText.pt } : {}),
    publishedAt: formData.publishedAt,
    useCaseSlug: useCaseSlug,
    ...(formData.pageId ? { pageId: formData.pageId } : {}),
    ...(formData.actionParams ? { actionParams: formData.actionParams } : {}),
    translations: buildTranslationArray(formData),
    items: buildItemPayloads(formData),
    isActive: false, // Ensure it stays inactive while editing
  };
}

// ---------------------------------------------------------------------------
// Multi-tenant creation
// ---------------------------------------------------------------------------

/** Where one tenant's copy of an announcement lands. */
export interface TenantTarget {
  /** Platform version, which is per tenant — versions do not line up across tenants. */
  platformVersionId: string;
  /** Page the announcement is scoped to; omitted means the tenant's main page. */
  pageId?: string;
  /**
   * Use cases the announcement is restricted to, from **this tenant's own** list — a tenant can
   * have several, and they do not carry across tenants.
   *
   * The BFF stores a single `useCaseSlug` per record, so picking more than one produces one record
   * per use case. An empty array means every user of the tenant.
   */
  useCaseSlugs: string[];
}

/**
 * One payload per tenant **and per use case**, sharing the same content.
 *
 * The BFF has neither a multi-tenant create nor a multi-use-case one: `useCaseSlug` is a single
 * value on the record. So a tenant with three use cases selected becomes three records, each
 * carrying that tenant's own version and page — neither of which is comparable across tenants.
 *
 * A tenant with no use case selected produces one record with no `useCaseSlug`, which the BFF
 * reads as "every user of the tenant".
 *
 * Tenants with no version chosen are skipped rather than sent with an empty one — the caller
 * decides whether that is a validation error.
 *
 * @param formData - The shared content. Its own `tenantSlug` / `platformVersionId` / `pageId` /
 *   `useCaseSlug` are ignored in favour of each target's.
 * @param targets - Target per tenant slug.
 */
export function buildMultiTenantPayloads(
  formData: WhatsNewFormData,
  targets: Record<string, TenantTarget>,
): ICreateWhatsNewParams[] {
  return Object.entries(targets)
    .filter(([, target]) => target.platformVersionId.length > 0)
    .flatMap(([tenantSlug, target]) => {
      const base = {
        ...formData,
        tenantSlug,
        platformVersionId: target.platformVersionId,
        pageId: target.pageId,
      };

      if (target.useCaseSlugs.length === 0) {
        return [buildCreatePayload({ ...base, useCaseSlug: undefined })];
      }

      return target.useCaseSlugs.map(useCaseSlug => buildCreatePayload({ ...base, useCaseSlug }));
    });
}

/** How many records {@link buildMultiTenantPayloads} will create, for the button's label. */
export function countPlannedRecords(targets: Record<string, TenantTarget>): number {
  return Object.values(targets)
    .filter(target => target.platformVersionId.length > 0)
    .reduce((total, target) => total + Math.max(1, target.useCaseSlugs.length), 0);
}
